// Seznam zkušebních v Sales reportu (15. 10. 2026, Láďa schválil variantu A).
// Nahoře dlaždice jako filtr (Vše, Proběhne, Proběhl, Zrušeno), pod nimi na počítači
// tabulka jako na stránce Sales > Zkušební, na telefonu řádky kontaktů. Prvních 10,
// zbytek za tlačítkem. Pořadí: nadcházející od nejbližšího, proběhlé od nejnovějšího, zrušené.
import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { DlazdiceFiltr } from "../ui.jsx";
import { useUzkaObrazovka } from "../../lib/obrazovka.js";
import { denKratce, casBezNuly } from "./salesApi.js";
import { Pil, RadekKontaktu } from "./salesUi.jsx";

const LIMIT = 10;
const VYSLEDEK = {
  probehne: { skupina: "probehne", text: "proběhne", ton: "znacka" },
  klientka: { skupina: "probehl", text: "klientka", ton: "ok" },
  prohra: { skupina: "probehl", text: "prohra", ton: "problem" },
  ceka: { skupina: "probehl", text: "čeká na rozhodnutí", ton: "pozor" },
  zruseno: { skupina: "zruseno", text: "zrušeno", ton: "problem" },
  nedorazila: { skupina: "zruseno", text: "nedorazila", ton: "problem" },
};
const FILTRY = [
  { key: "vse", popisek: "Vše", ton: "znacka" },
  { key: "probehne", popisek: "Proběhne", ton: "znacka" },
  { key: "probehl", popisek: "Proběhl", ton: "ok" },
  { key: "zruseno", popisek: "Zrušeno", ton: "problem" },
];
const PORADI = { probehne: 0, probehl: 1, zruseno: 2 };
const klic = (z) => `${z.datum || "9999-99-99"} ${String(z.cas || "").padStart(5, "0")}`;
const krestni = (t) => String(t || "").split(" ")[0];

export default function ZkusebniReportSeznam({ seznam }) {
  const uzka = useUzkaObrazovka();
  const navigate = useNavigate();
  const [filtr, setFiltr] = useState("vse");
  const [vse, setVse] = useState(false);

  const serazene = useMemo(() => (seznam || [])
    .map((z) => ({ ...z, v: VYSLEDEK[z.vysledek] || VYSLEDEK.ceka }))
    .sort((a, b) => {
      const s = PORADI[a.v.skupina] - PORADI[b.v.skupina];
      if (s) return s;
      return a.v.skupina === "probehne" ? klic(a).localeCompare(klic(b)) : klic(b).localeCompare(klic(a));
    }), [seznam]);

  if (!serazene.length) return <div className="text-sm text-slate-500">Sales v tomhle období žádný zkušební nedomluvila.</div>;

  const pocet = (k) => (k === "vse" ? serazene.length : serazene.filter((z) => z.v.skupina === k).length);
  const vybrane = filtr === "vse" ? serazene : serazene.filter((z) => z.v.skupina === filtr);
  const zobrazene = vse ? vybrane : vybrane.slice(0, LIMIT);
  const termin = (z) => (z.datum ? denKratce(z.datum) : "bez termínu");
  const odkaz = (z) => (z.lead_id ? `/sales/kontakt/${z.lead_id}` : null);
  const tlacitko = (z) => odkaz(z)
    ? <Link to={odkaz(z)} className="btn-ikona" aria-label={`Otevřít ${z.jmeno}`} title="Otevřít kartu"><ArrowUpRight className="h-5 w-5" /></Link>
    : null;

  return (
    <div>
      <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
        {FILTRY.map((f) => (
          <DlazdiceFiltr key={f.key} popisek={f.popisek} hodnota={pocet(f.key)} ton={f.ton} zapnuto={filtr === f.key}
            onClick={() => { setFiltr(f.key); setVse(false); }} />
        ))}
      </div>
      {!zobrazene.length ? <div className="mt-4 text-sm text-slate-500">V tomhle výběru nic není.</div> : uzka ? (
        <div className="-mx-4 mt-4 sm:-mx-5">
          {zobrazene.map((z) => (
            <RadekKontaktu key={z.id} prvni={false} jmeno={<span className="demo-sensitive">{z.jmeno}</span>}
              onOtevrit={odkaz(z) ? () => navigate(odkaz(z)) : undefined}
              pilulky={<><Pil ton="znacka" casti={[termin(z), z.cas ? casBezNuly(z.cas) : null, krestni(z.trener)]} />{z.v.skupina !== "probehne" && <Pil ton={z.v.ton} casti={z.v.text} />}</>} />
          ))}
        </div>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500"><th className="px-3 py-2">Termín</th><th className="px-3 py-2">Zájemkyně</th><th className="px-3 py-2">Trenér</th><th className="px-3 py-2">Výsledek</th><th className="px-3 py-2" /></tr></thead>
            <tbody>
              {zobrazene.map((z) => (
                <tr key={z.id} className="border-t border-slate-100">
                  <td className="whitespace-nowrap px-3 py-2.5"><div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{termin(z)}</div>{z.cas && <div className="font-bold tabular-nums text-slate-900">{casBezNuly(z.cas)}</div>}</td>
                  <td className="px-3 py-2.5 font-bold text-brand-deep demo-sensitive">{z.jmeno}</td>
                  <td className="px-3 py-2.5">{krestni(z.trener) || "neuvedeno"}</td>
                  <td className="px-3 py-2.5"><Pil ton={z.v.ton} casti={z.v.text} mala /></td>
                  <td className="px-3 py-2.5 text-right">{tlacitko(z)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!vse && vybrane.length > LIMIT && (
        <div className="mt-4"><button type="button" className="btn-secondary" onClick={() => setVse(true)}>Zobrazit všech {vybrane.length}</button></div>
      )}
    </div>
  );
}
